import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { CONNECTOR_LABEL, useConnections } from '@/entities/connection';
import { groupByConnection, useDatasets } from '@/features/catalog-browser';
import type { DatasetGroup } from '@/features/catalog-browser';

/* The catalogue the Suggestions pickers read: one page of datasets grouped by source, with each
 * source's connector family. The same cached read the Datasets and Lineage screens make, so a
 * source's name and table count cannot disagree between them. */

/** The catalogue page the picker reads. A deliberate bound, stated on screen. */
export const PICKER_DATASETS = 200;

export interface Catalogue {
  readonly datasets: ReturnType<typeof useDatasets>;
  readonly groups: readonly DatasetGroup[];
  readonly types: ReadonlyMap<string, string>;
}

export function useSuggestionCatalogue(): Catalogue {
  const datasets = useDatasets({ search: '', connectionId: null });
  const connections = useConnections();

  const names = useMemo(
    () => new Map((connections.data?.items ?? []).map((each) => [each.id, each.name])),
    [connections.data],
  );
  const types = useMemo(
    () =>
      new Map(
        (connections.data?.items ?? []).map((each) => [
          each.id,
          CONNECTOR_LABEL[each.connector_type],
        ]),
      ),
    [connections.data],
  );
  const groups = useMemo(() => {
    if (!datasets.isSuccess) return [];
    return groupByConnection(datasets.data.items, {
      status: connections.isPending
        ? 'loading'
        : connections.isError
          ? 'unreadable'
          : 'ready',
      names,
    });
  }, [
    datasets.isSuccess,
    datasets.data,
    connections.isPending,
    connections.isError,
    names,
  ]);

  return { datasets, groups, types };
}

/** A group's display name: the source's own name, or why it cannot be named. */
export function useGroupName(): (group: DatasetGroup) => string {
  const { t } = useTranslation();
  return (group) =>
    group.label.kind === 'named'
      ? group.label.name
      : t(`catalog.group.${group.label.kind}`);
}
